<template>
  <VBusHelper
    :reactOn="$constants?.EVENTS?.SHARER?.DISH?.SHOW"
    :handler="(data) => show(data)"
  />
  <DialogComponent
    ref="modal"
    type="bottomsheet"
    noInnerPadding
    hideCloseButton
  >
    <div class="w-full h-full flex flex-col overflow-auto gap-2 px-4">
      <div class="mobile-status-padding"></div>

      <div class="title-color text-2xl font-bold text-center mb-2">
        Partager le plat
      </div>

      <!-- Dish Preview -->
      <div
        v-if="dishData"
        class="mb-6 p-4 rounded-2xl bg-white/5 border border-white/10"
      >
        <div class="flex items-center gap-4">
          <div class="w-16 h-16 rounded-xl overflow-hidden shrink-0">
            <ImgComponent
              :src="dishImage"
              class="w-full h-full object-cover"
            />
          </div>
          <div class="flex-1 min-w-0">
            <p class="title-color font-semibold truncate text-base">
              {{ dishName }}
            </p>
            <p class="text-primary font-bold text-sm mt-1">
              {{ formatPrice(dishData.price) }}
            </p>
          </div>
        </div>
      </div>

      <div class="gap-6">
        <!-- Native Share Button -->
        <button
          v-if="supportsNativeShare"
          @click="shareNative"
          class="w-full ripple rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 py-4 px-6 flex items-center justify-center gap-3 title-color font-semibold shadow-lg mb-6 transition-all active:scale-95"
          :disabled="isSharing"
        >
          <svg
            v-if="!isSharing"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
            <polyline points="16 6 12 2 8 6"></polyline>
            <line x1="12" y1="2" x2="12" y2="15"></line>
          </svg>
          <svg
            v-else
            class="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span>{{
            isSharing ? 'Préparation...' : "Partager via les applications"
          }}</span>
        </button>

        <!-- Sharing options (grille) -->
        <div class="grid grid-cols-4 gap-y-6 gap-x-2 mb-6">
          <div
            class="flex flex-col items-center gap-2 justify-center cursor-pointer hover:scale-105 transition-transform"
            v-for="(sharing, i) in socialSharings"
            :key="i"
            v-bind="sharing.type !== 'copy' ? { 'data-sharer': sharing.type, 'data-title': shareText, 'data-url': shareUrl } : {}"
            @click="sharing.type === 'copy' ? copyLink() : undefined"
          >
            <div
              class="ripple rounded-full w-16 h-16 flex items-center justify-center p-5"
              :class="[sharing.class]"
            >
              <ImgComponent
                :src="sharing.logo"
                alt=""
                class="w-full h-full object-contain"
              />
            </div>
            <div class="text-sm title-color font-semibold">
              {{ sharing.name }}
            </div>
          </div>
        </div>

        <!-- Bouton copier large -->
        <button
          @click="copyLink"
          class="w-full ripple rounded-xl bg-gradient-to-r from-primary to-orange-600 py-4 px-6 flex items-center justify-center gap-3 text-white font-semibold shadow-lg"
        >
          <ColorImg
            src="/images/all/copy.svg"
            alt=""
            class="w-5 h-5 bg-white"
          />
          <span>Copier le lien</span>
        </button>

        <div class="py-4"></div>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import { importSharerJs, initSharrerWatch } from '../importSharerJs';
import Commons from '../../shared/libraries/commons';

const { proxy } = getCurrentInstance();
const { $bus, $constants } = proxy;

const modal = ref(null);
const dishData = ref(null);
const supportsNativeShare = ref(false);
const isSharing = ref(false);

const dishName = computed(() => {
  if (!dishData.value) return 'Un plat délicieux';
  return dishData.value.name || 'Un plat délicieux';
});

const dishImage = computed(() => {
  console.log("🚀 ~ dishData:", dishData)
  if (!dishData.value) return '';
  return dishData.value.principalImageUrl || '';
});

const formatPrice = (price) => {
  if (price == null) return '';
  return Commons.formatNumber(price, { suffix: ' CFA' });
};

const shareText = computed(() => {
  return `🤤 J'ai trouvé cette pépite sur Rapidoo : "${dishName.value}" à ${formatPrice(dishData.value?.price)} !\n\nViens le découvrir 👇 \n\n`;
});

const shareUrl = computed(() => {
  if (!dishData.value) return window.location.href;
  const encodedData = btoa(JSON.stringify({ t: 'd', id: dishData.value.id }));
  const baseUrl = $constants?.URLS?.dishSharingBaseUrl || 'https://app.monrapidoo.com';
  return `${baseUrl}/shared/${encodedData}`;
});

const socialSharings = computed(() => {
  return $constants?.SHARINGS_OPTIONS || [];
});

const show = (data) => {
  dishData.value = data;
  modal.value?.show();
};

defineExpose({
  show
});

const shareNative = async () => {
  if (isSharing.value) return;
  isSharing.value = true;

  const imageUrl = dishImage.value;
  let files = [];
  
  try {
    if (imageUrl) {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const file = new File(
        [blob],
        `dish-${dishData.value?.id || 'rapidoo'}.jpg`,
        { type: blob.type }
      );
      files = [file];
    }
  } catch (error) {
    console.warn("Impossible de charger l'image pour le partage natif", error);
  }

  try {
    const shareData = {
      title: dishName.value,
      text: shareText.value,
      url: shareUrl.value
    };
    if (files.length > 0) {
      shareData.files = files;
    }
    await Commons.navigatorShare(shareData);
    modal.value?.hide();
  } catch (error) {
    console.error('Le partage natif a échoué', error);
  } finally {
    isSharing.value = false;
  }
};

const copyLink = () => {
  Commons.copyToClipboard(
    shareUrl.value,
    () => {
      $bus.emit(
        $constants?.EVENTS?.TOAST_NOTIFICATION?.SHOW || 'toast.show',
        {
          title: 'Lien copié',
          body: 'Le lien du plat a été copié dans le presse-papier'
        }
      );
      modal.value?.hide();
    },
    (error) => {
      $bus.emit(
        $constants?.EVENTS?.TOAST_NOTIFICATION?.SHOW || 'toast.show',
        {
          title: 'Erreur',
          body: error?.message || 'Impossible de copier le lien'
        }
      );
    }
  );
};

onMounted(async () => {
  if (await Commons.supportNativeShare()) {
    supportsNativeShare.value = true;
  }
  importSharerJs().then(() => {
    initSharrerWatch();
  });
});
</script>
